/**
 * Composer.tsx — the start page: a library manager.
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ What does your model look like?              [＋ New] [🔌 API] │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ [ Library · Import ]                                              │
 *   │                                                                    │
 *   │ Library: search + tag-filter chips, then the user's file rows.    │
 *   │   Each row: Open · Edit · ⋯ (Duplicate · Rename · Tags · Export · │
 *   │   Delete). Rename edits the title; Tags opens an inline editor.   │
 *   │ Import: pick a source (ZIP archive, git over SSH or HTTPS with    │
 *   │   stored per-host credentials, or a Forgejo instance like         │
 *   │   vski.sh) → follow the flow → save → open.                       │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Grouping is by user-assigned TAGS (a flat, proven filter pattern), not by
 * hard-coded sections — the user opts into structure rather than inheriting
 * it. The adapter is injected; the manager never knows where files live.
 * Demo/template projects are ordinary library entries: the backend seeds
 * them into the user's data dir on first run.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowClockwise,
  ArrowUp,
  CheckCircle,
  CopySimple,
  DownloadSimple,
  FileArchive,
  FloppyDisk,
  GitBranch,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  SpinnerGap,
  Tag,
  TestTube,
  Trash,
  UploadSimple,
  WarningCircle,
  X,
} from '@phosphor-icons/react';

import type { ScenarioBundle } from '../engine/types';
import type { LibraryAdapter, LibraryEntry } from '../library/LibraryAdapter';
import type { GitCredentialInput, GitLookup } from '../projects/RemoteProjectAdapter';
import { uuid } from '../library/ids';
import { LibraryError } from '../library/LibraryAdapter';
import { useAuth } from '../auth/AuthContext';
import { useLibrary } from '../library/useLibrary';
import { ConnectivityButton } from '../connect/ConnectivityButton';
import { ConnectivityModal } from '../connect/ConnectivityModal';
import { ConnectivityWizard } from '../connect/ConnectivityWizard';
import { useConnectivity } from '../connect/useConnectivity';
import { httpOriginOf } from '../connect/httpOrigin';
import { DotsMenu } from '../panels/DotsMenu';
import { DockerIcon } from '../panels/StatusBar';
import { ServicesModal } from '../panels/ServicesModal';
import { UpdateModal } from '../panels/UpdateModal';
import { useVersionCheck } from '../version/useVersionCheck';
import { useConfirm } from '../panels/ConfirmDialog';
import { usePrompt } from '../panels/PromptDialog';

type Tab = 'library' | 'import';

interface ComposerProps {
  adapter: LibraryAdapter;
  /** Open by entry. `mode` selects read-only viewer ('view') vs full editor
   *  ('edit'); it becomes the `/doc/{id}/{mode}` route suffix. */
  onOpenEntry: (entry: LibraryEntry, mode: 'view' | 'edit') => void;
  /** Create a new blank project. The name is prompted in the Composer (not
   *  App) so the handler receives the user-chosen label + uses it for the
   *  project's display name + slug derivation. */
  onBlank: (name: string) => void;
}

export function Composer({
  adapter,
  onOpenEntry,
  onBlank,
}: ComposerProps): JSX.Element {
  const [tab, setTab] = useState<Tab>('library');
  const [showConnectivity, setShowConnectivity] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  // The automatic version checker (frontend drift vs the served manifest +
  // stack drift vs the live backend digests — see src/version/).
  const versionCheck = useVersionCheck();
  // The library body is REPLACED by the Connectivity wizard while the app is
  // not connected — there is nothing to browse without a backend. `checking`
  // gates the swap so a healthy backend never flashes the wizard on boot.
  const connectivity = useConnectivity();
  // The services button/modal needs the backend origin + auth token (the same
  // derivation Studio uses); only meaningful when actually connected.
  const auth = useAuth();
  const backendOrigin = auth.wsUrl ? httpOriginOf(auth.wsUrl, '') : null;
  const { entries, loading, error, query, setQuery, refresh } = useLibrary(adapter);
  const confirm = useConfirm();
  const prompt = usePrompt();

  // ── Row actions ────────────────────────────────────────────────────────
  const duplicate = async (entry: LibraryEntry) => {
    try {
      const name = await prompt({
        title: 'Duplicate file',
        body: 'Save a copy under a new name:',
        defaultValue: `${entry.label} copy`,
      });
      if (!name) return;
      // For a backend project, duplicate the FULL package (graph + manifest +
      // src/ + data/) so the copy keeps its model + tables + code. A graph-
      // only importBundle would drop all of that. Feature-detect: only
      // RemoteProjectAdapter has duplicate(); the local adapter uses
      // importBundle (a local copy is graph-only by design — no backend model).
      //
      // IMPORTANT: navigate via onOpenEntry(created, 'edit'), NOT onOpenBundle.
      // The project already exists on the backend after duplicate() — fetching
      // its graph via adapter.get() returns a ScenarioBundle whose internal id
      // is the GRAPH id ("main"), not the project UUID. Passing that to
      // onOpenBundle would re-import it as a SECOND project keyed by "main",
      // producing the spurious "Main" entry alongside the real duplicate.
      // onOpenEntry navigates by the project UUID + rewrites the bundle id to
      // match (App.tsx:186-188), which is the correct id-resolved path.
      if (typeof (adapter as unknown as { duplicate?: unknown }).duplicate === 'function') {
        const created = await (adapter as unknown as { duplicate: (id: string, opts?: { label?: string }) => Promise<LibraryEntry> }).duplicate(entry.id, { label: name });
        refresh();
        onOpenEntry(created, 'edit');
        return;
      }
      const bundle = await adapter.get(entry.id);
      if (!bundle) return;
      const created = await adapter.importBundle({ ...bundle, label: name }, { slug: name });
      refresh();
      onOpenEntry(created, 'edit');
    } catch (e) {
      void e;
    }
  };

  // Prompt for a project name BEFORE creating — the name drives the display
  // label + the slug derivation on the backend. Without this, every "New"
  // created an "Untitled" project and the user had to rename afterward.
  const handleBlank = async () => {
    const name = await prompt({
      title: 'New project',
      body: 'Enter a name for your project:',
      defaultValue: 'Untitled',
    });
    if (!name?.trim()) return;
    onBlank(name.trim());
  };

  // Rename edits the TITLE. The adapter re-derives the slug.
  const rename = async (entry: LibraryEntry) => {
    const name = await prompt({ title: 'Rename', defaultValue: entry.label });
    if (name === null || name === entry.label) return;
    try {
      await adapter.rename(entry.id, name);
      refresh();
    } catch (e) {
      if (e instanceof LibraryError && e.code === 'collision') {
        await confirm({ title: 'Name taken', body: `“${name}” already exists.`, confirmLabel: 'OK' });
      }
    }
  };

  const exportEntry = async (entry: LibraryEntry) => {
    // Always export as a ZIP — a project is a full package (multiple graphs +
    // manifest + src/ + data/ + tables), not a single graph JSON. The backend's
    // /download endpoint returns the complete archive. Feature-detect download
    // (only RemoteProjectAdapter has it); fall back to a graph-only JSON export
    // for local mode (no backend to package the full project).
    if (typeof (adapter as unknown as { download?: unknown }).download === 'function') {
      try {
        const zip = await (
          adapter as unknown as { download: (id: string) => Promise<Blob> }
        ).download(entry.id);
        const url = URL.createObjectURL(zip);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${entry.slug || entry.id}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      } catch {
        // Backend download failed — fall through to the JSON fallback below.
      }
    }
    // Local-mode fallback: export the primary graph as JSON (best effort —
    // doesn't include src/ + data/ + tables, but better than nothing).
    const bundle = await adapter.get(entry.id);
    if (!bundle) return;
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entry.slug || entry.id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const remove = async (entry: LibraryEntry) => {
    const ok = await confirm({
      title: 'Delete file?',
      body: <>Delete <strong>{entry.label}</strong>? This cannot be undone.</>,
      danger: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await adapter.delete(entry.id);
      refresh();
    } catch {
      // ignore — refresh keeps the UI honest
    }
  };

  return (
    <>
    <div style={{
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        gap: "8px",
        position: "absolute",
        top: "1em",
        right: "1em",
        zIndex: 1000
      }}>
        {/* Update available (the automatic version checker) — the pill opens
            the Update modal (frontend reload / stack update). */}
        {(versionCheck.frontendUpdate === true || versionCheck.stackUpdate === true) && (
          <button
            type="button"
            className="connectivity-btn svc-btn update-pill"
            onClick={() => setShowUpdate(true)}
            title="Update available — open to review and apply"
            aria-label="Update available"
          >
            <ArrowClockwise size={15} weight="bold" />
            <span>Update</span>
          </button>
        )}
        {/* All-projects services (docker containers) — only when a remote
            backend is actually connected; sits BEFORE the connectivity
            button. The modal opens unscoped (every app-managed container). */}
        {connectivity.derived.connected && backendOrigin && (
          <button
            type="button"
            className="connectivity-btn svc-btn"
            onClick={() => setShowServices(true)}
            title="Services — running docker containers (all projects)"
            aria-label="Services"
          >
            <DockerIcon />
            <span></span>
          </button>
        )}
        <ConnectivityButton
          connected={connectivity.derived.connected}
          onClick={() => setShowConnectivity(true)}
        />
      </div>
    <div className="composer lib-manager-shell">
      {connectivity.checking ? (
        <div className="composer-inner lib-manager">
          <p className="api-connect-hint">Checking connectivity…</p>
        </div>
      ) : !connectivity.derived.connected ? (
        <div className="composer-inner lib-manager conn-wizard-host">
          <ConnectivityWizard embedded />
        </div>
      ) : (
      <div className="composer-inner lib-manager">
        <header className="lib-manager-head">
          <div>
            {/* <h1>What does your model look like?</h1>
            <p className="composer-sub">
              Open a file from your library, start from a template, build from scratch, or connect
              to a remote backend.
            </p> */}
          </div>
          <div className="lib-manager-actions">
            <button type="button" className="btn btn-start" onClick={handleBlank}>
              <Plus size={14} weight="bold" /> New
            </button>

          </div>
        </header>

        <nav className="lib-tabs" role="tablist">
          <TabButton tab="library" active={tab} onClick={setTab} count={entries.length} />
          <TabButton tab="import" active={tab} onClick={setTab} />
        </nav>

        {tab === 'library' && (
          <LibraryTab
            entries={entries}
            loading={loading}
            error={error}
            query={query}
            setQuery={setQuery}
            onEdit={(e) => onOpenEntry(e, 'edit')}
            onOpen={(e) => onOpenEntry(e, 'edit')}
            onDuplicate={duplicate}
            onRename={rename}
            onExport={exportEntry}
            onDelete={remove}
            onSetTags={async (entry, tags) => {
              try {
                await adapter.setTags(entry.id, tags);
                refresh();
              } catch {
                /* ignore */
              }
            }}
          />
        )}

        {tab === 'import' && (
          <ImportTab
            adapter={adapter}
            onImported={(entry) => {
              refresh();
              // Navigate by the project UUID — NOT via onOpenBundle (which
              // re-imports the graph as a new project, same bug as the old
              // duplicate handler). onOpenEntry fetches + caches correctly.
              onOpenEntry(entry, 'edit');
            }}
          />
        )}
      </div>
      )}
    </div>
    <ConnectivityModal isOpen={showConnectivity} onClose={() => setShowConnectivity(false)} />
    {showServices && (
      <ServicesModal
        backendOrigin={backendOrigin}
        token={auth.token}
        onClose={() => setShowServices(false)}
      />
    )}
    {showUpdate && <UpdateModal onClose={() => setShowUpdate(false)} />}
    </>
  );
}

// ─── Tab button ─────────────────────────────────────────────────────────────
function TabButton({
  tab,
  active,
  onClick,
  count,
}: {
  tab: Tab;
  active: Tab;
  onClick: (t: Tab) => void;
  count?: number;
}) {
  const label = tab === 'library' ? 'Library' : 'Import';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active === tab}
      className={`lib-tab${active === tab ? ' active' : ''}`}
      onClick={() => onClick(tab)}
    >
      {label}
      {count !== undefined && count > 0 && <span className="lib-tab-count">{count}</span>}
    </button>
  );
}

// ─── Library tab (user files, tag-filtered) ─────────────────────────────────
function LibraryTab({
  entries,
  loading,
  error,
  query,
  setQuery,
  onEdit,
  onOpen: _onOpen,
  onDuplicate,
  onRename,
  onExport,
  onDelete,
  onSetTags,
}: {
  entries: LibraryEntry[];
  loading: boolean;
  error: LibraryError | null;
  query: string;
  setQuery: (q: string) => void;
  onEdit: (e: LibraryEntry) => void;
  onOpen: (e: LibraryEntry) => void;
  onDuplicate: (e: LibraryEntry) => void;
  onRename: (e: LibraryEntry) => void;
  onExport: (e: LibraryEntry) => void;
  onDelete: (e: LibraryEntry) => void;
  onSetTags: (e: LibraryEntry, tags: string[]) => void;
}) {
  // Tag filter: the distinct tags across the user's files, multi-select.
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const e of entries) for (const t of e.tags) set.add(t);
    return [...set].sort();
  }, [entries]);
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [tagging, setTagging] = useState<string | null>(null); // entry id being edited

  const visible = useMemo(() => {
    if (activeTags.size === 0) return entries;
    return entries.filter((e) => e.tags.some((t) => activeTags.has(t)));
  }, [entries, activeTags]);

  const toggleTag = (t: string) =>
    setActiveTags((cur) => {
      const next = new Set(cur);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });

  return (
    <div className="lib-tab-panel">
      <div className="lib-search">
        <MagnifyingGlass size={15} />
        <input
          type="text"
          placeholder="Search files…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
        {loading && <SpinnerGap size={15} className="spin" />}
      </div>

      {allTags.length > 0 && (
        <div className="tag-filter" role="toolbar" aria-label="Filter by tag">
          {allTags.map((t) => (
            <button
              key={t}
              type="button"
              className={`tag-chip${activeTags.has(t) ? ' active' : ''}`}
              aria-pressed={activeTags.has(t)}
              onClick={() => toggleTag(t)}
            >
              <Tag size={11} /> {t}
            </button>
          ))}
          {activeTags.size > 0 && (
            <button type="button" className="tag-clear" onClick={() => setActiveTags(new Set())}>
              Clear
            </button>
          )}
        </div>
      )}

      {error && <div className="lib-error">{error.message}</div>}

      <div className="lib-list">
        {visible.length === 0 ? (
          <div className="lib-row-empty">
            {loading
              ? 'Loading…'
              : entries.length === 0
                ? 'Your library is empty. Create one with New or import a project from the Import tab.'
                : `No files match${activeTags.size ? ' these tags' : '“' + query + '”'}.`}
          </div>
        ) : (
          visible.map((e) => (
            <LibraryRow key={e.id} entry={e} tagging={tagging === e.id}>
              {/* <button type="button" className="btn btn-start btn-sm" onClick={() => onOpen(e)}>
                <Play size={12} weight="fill" /> Open
              </button> */}
              <button type="button" className="btn btn-sm" onClick={() => onEdit(e)}>
                <PencilSimple size={12} /> Edit
              </button>
              <DotsMenu
                items={[
                  { label: 'Duplicate', icon: <CopySimple size={13} />, onSelect: () => onDuplicate(e) },
                  { label: 'Rename', icon: <PencilSimple size={13} />, onSelect: () => onRename(e) },
                  { label: 'Tags', icon: <Tag size={13} />, onSelect: () => setTagging(tagging === e.id ? null : e.id) },
                  { label: 'Export', icon: <DownloadSimple size={13} />, onSelect: () => onExport(e) },
                  { label: 'Delete', icon: <Trash size={13} />, danger: true, onSelect: () => onDelete(e) },
                ]}
              />
              {tagging === e.id && (
                <TagEditor
                  tags={e.tags}
                  onSave={(tags) => {
                    onSetTags(e, tags);
                    setTagging(null);
                  }}
                  onCancel={() => setTagging(null)}
                />
              )}
            </LibraryRow>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Import tab ──────────────────────────────────────────────────────────────
// One selectbox, four flows: ZIP archive (drag-drop upload), git over SSH,
// git over HTTPS, and Forgejo (vski.sh — API archive download, no git
// history). All flows need a backend connection (the local adapter has
// none of these methods — feature-detected). The git flows center on a
// pasted URL: a debounced lookup asks the backend whether it holds a
// credential for the URL's host (encrypted keystore, backend/gitcreds.py)
// and the form adapts — stored credentials are used silently, missing ones
// get a paste form (SSH key / user+password / PAT) with an optional
// "save for this host" checkbox and a Test button (git ls-remote).
type ImportSource = 'zip' | 'git-ssh' | 'git-https' | 'forgejo';

/** The git-import surface of RemoteProjectAdapter, feature-detected on the
 *  injected LibraryAdapter. */
interface GitImportAdapter {
  importGit(
    url: string,
    opts?: { label?: string; ref?: string; credential?: GitCredentialInput; saveCredential?: boolean },
  ): Promise<LibraryEntry>;
  importForgejo(
    url: string,
    opts?: { label?: string; ref?: string; token?: string; saveToken?: boolean; username?: string },
  ): Promise<LibraryEntry>;
  gitLookup(url: string): Promise<GitLookup>;
  gitTest(url: string, credential?: GitCredentialInput): Promise<{ ok: boolean; detail?: string }>;
  listGitCredentials(): Promise<{ enabled: boolean }>;
}

/** Human label for a stored credential ("SSH key (git)", "password (anton)",
 *  "personal access token") — used by both git forms' status lines. */
function describeCredential(cred: { kind: string; username?: string | null }): string {
  const who = cred.username ? ` (${cred.username})` : '';
  if (cred.kind === 'ssh_key') return `SSH key${who}`;
  if (cred.kind === 'pat') return `personal access token${who}`;
  return `password${who}`;
}

/** Debounce a changing value (the URL inputs' lookup trigger). */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function ImportTab({
  adapter,
  onImported,
}: {
  adapter: LibraryAdapter;
  onImported: (entry: LibraryEntry) => void;
}) {
  const [source, setSource] = useState<ImportSource>('zip');
  // Feature-detect the git surface (only RemoteProjectAdapter has it; the
  // ZIP path feature-detects importZip separately below).
  const gitAdapter = adapter as unknown as Partial<GitImportAdapter>;
  const hasGit = typeof gitAdapter.importGit === 'function';

  if (!hasGit) {
    return (
      <div className="lib-tab-panel upload-panel">
        <div className="upload-dropzone disabled">
          <UploadSimple size={28} />
          <div className="upload-dropzone-title">Backend required</div>
          <div className="upload-dropzone-sub">
            Importing projects requires a backend connection. Use{' '}
            <strong>Connect API</strong> to connect to a running backend first.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lib-tab-panel upload-panel">
      <label className="upload-field import-source">
        <select
          className="import-select"
          value={source}
          onChange={(e) => setSource(e.target.value as ImportSource)}
        >
          <option value="zip">ZIP archive</option>
          <option value="git-ssh">Git repository (SSH)</option>
          <option value="git-https">Git repository (HTTPS)</option>
          <option value="forgejo">Forgejo (vski.sh)</option>
        </select>
      </label>

      {source === 'zip' && <ZipImportForm adapter={adapter} onImported={onImported} />}
      {(source === 'git-ssh' || source === 'git-https') && (
        <GitImportForm
          adapter={gitAdapter as GitImportAdapter}
          mode={source === 'git-ssh' ? 'ssh' : 'https'}
          onImported={onImported}
        />
      )}
      {source === 'forgejo' && (
        <ForgejoImportForm adapter={gitAdapter as GitImportAdapter} onImported={onImported} />
      )}
    </div>
  );
}

// ─── ZIP flow (the original upload: drag-drop → name → save → open) ─────────
// Accepts a project ZIP archive (the full package: graph + manifest + src/ +
// data/), not a bare JSON graph. A project can have multiple graphs + tables +
// model code — none of which survive a JSON-only export. The ZIP is uploaded
// to the backend via RemoteProjectAdapter.importZip, which mints a fresh UUID,
// uploads, and applies the user's label.
type UploadState =
  | { kind: 'idle' }
  | { kind: 'selected'; fileName: string; fileSize: number }
  | { kind: 'saving'; fileName: string }
  | { kind: 'error'; message: string };

function ZipImportForm({
  adapter,
  onImported,
}: {
  adapter: LibraryAdapter;
  onImported: (entry: LibraryEntry) => void;
}) {
  const [state, setState] = useState<UploadState>({ kind: 'idle' });
  const [dragging, setDragging] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileRef = useRef<File | null>(null);

  const handleFile = async (file: File) => {
    // Basic validation: must be a .zip. We don't parse the archive client-side
    // — the backend unpacks it and returns a validation error if it's malformed.
    if (!file.name.toLowerCase().endsWith('.zip') && file.type !== 'application/zip') {
      setState({ kind: 'error', message: 'Please select a .zip project archive.' });
      return;
    }
    fileRef.current = file;
    // Default the name to the filename without the .zip extension.
    const baseName = file.name.replace(/\.zip$/i, '');
    setPendingName(baseName);
    setState({ kind: 'selected', fileName: file.name, fileSize: file.size });
  };

  const handleSave = async () => {
    const file = fileRef.current;
    if (!file || !pendingName.trim()) return;
    setState({ kind: 'saving', fileName: file.name });
    try {
      const entry = await (
        adapter as unknown as {
          importZip: (zip: Blob, opts?: { label?: string }) => Promise<LibraryEntry>;
        }
      ).importZip(file, { label: pendingName.trim() });
      fileRef.current = null;
      setPendingName('');
      setState({ kind: 'idle' });
      onImported(entry);
    } catch (e) {
      const msg = e instanceof LibraryError ? e.message : 'Upload failed.';
      setState({ kind: 'error', message: msg });
    }
  };

  const reset = () => {
    fileRef.current = null;
    setPendingName('');
    setState({ kind: 'idle' });
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />

      {state.kind === 'idle' || state.kind === 'error' ? (
        <>
          <div
            className={`upload-dropzone${dragging ? ' dragging' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
          >
            <UploadSimple size={28} />
            <div className="upload-dropzone-title">Drop a project archive here</div>
            <div className="upload-dropzone-sub">or click to choose a .zip file</div>
          </div>
          {state.kind === 'error' && (
            <div className="upload-error">
              <strong>Couldn't import:</strong> {state.message}
              <button type="button" className="btn btn-sm" onClick={reset}>
                Try again
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="upload-progress-card">
          <div className="upload-progress-head">
            <ArrowUp size={16} />
            <span className="upload-file-name">
              {state.fileName}
              {state.kind === 'selected' && (
                <span className="upload-file-size">
                  {' '}
                  ({(state.fileSize / 1024).toFixed(0)} KB)
                </span>
              )}
            </span>
          </div>

          {state.kind === 'selected' && (
            <>
              <div className="upload-done">Project archive ready to import.</div>
              <label className="upload-field">
                <span>Save as</span>
                <input
                  type="text"
                  className="text-input"
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  autoFocus
                />
              </label>
              <div className="upload-actions">
                <button type="button" className="btn" onClick={reset}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-start"
                  onClick={handleSave}
                  disabled={!pendingName.trim()}
                >
                  <FloppyDisk size={14} /> Upload &amp; open
                </button>
              </div>
            </>
          )}
          {state.kind === 'saving' && (
            <div className="upload-bar">
              <div className="upload-bar-fill indeterminate" />
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─── Git flows (SSH / HTTPS) ─────────────────────────────────────────────────
// The user pastes a repository URL; a debounced lookup checks the backend for
// a stored credential for the URL's host. The credential's kind dictates the
// transport server-side (an SSH key converts https URLs to ssh and vice
// versa), so the mode mainly preselects which credential form is offered.
function GitImportForm({
  adapter,
  mode,
  onImported,
}: {
  adapter: GitImportAdapter;
  mode: 'ssh' | 'https';
  onImported: (entry: LibraryEntry) => void;
}) {
  const [url, setUrl] = useState('');
  const [lookup, setLookup] = useState<GitLookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [credOpen, setCredOpen] = useState(false);
  const [credKind, setCredKind] = useState<'ssh_key' | 'password' | 'pat'>(
    mode === 'ssh' ? 'ssh_key' : 'pat',
  );
  const [username, setUsername] = useState('');
  const [secret, setSecret] = useState('');
  const [saveCred, setSaveCred] = useState(true);
  const [canSave, setCanSave] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail?: string } | null>(null);
  const [saveAs, setSaveAs] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lookupSeq = useRef(0);
  const saveAsDirty = useRef(false);

  // Can we persist credentials at all (keystore wired)?
  useEffect(() => {
    adapter
      .listGitCredentials()
      .then((s) => setCanSave(s.enabled))
      .catch(() => setCanSave(false));
  }, [adapter]);

  const debouncedUrl = useDebounced(url, 350);
  useEffect(() => {
    const seq = ++lookupSeq.current;
    const trimmed = debouncedUrl.trim();
    if (!trimmed) {
      setLookup(null);
      setLookupError(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    adapter
      .gitLookup(trimmed)
      .then((lk) => {
        if (seq !== lookupSeq.current) return;
        setLookup(lk);
        setLookupError(null);
        if (!saveAsDirty.current) setSaveAs(lk.repo_name);
      })
      .catch((e) => {
        if (seq !== lookupSeq.current) return;
        setLookup(null);
        setLookupError(e instanceof LibraryError ? e.message : 'Could not parse that URL.');
      })
      .finally(() => {
        if (seq === lookupSeq.current) setChecking(false);
      });
  }, [debouncedUrl, adapter]);

  const host = lookup?.host ?? null;
  const credComplete =
    !credOpen ||
    (secret.trim().length > 0 && (credKind === 'ssh_key' || credKind === 'pat' || username.trim().length > 0));

  const handleTest = async () => {
    if (!url.trim() || !credComplete) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await adapter.gitTest(url.trim(), credOpen ? { kind: credKind, username: username.trim() || undefined, secret } : undefined);
      setTestResult(result);
    } catch (e) {
      setTestResult({ ok: false, detail: e instanceof LibraryError ? e.message : 'Test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const handleImport = async () => {
    const trimmed = url.trim();
    if (!trimmed || !credComplete) return;
    if (credOpen && credKind === 'password' && !username.trim()) {
      setError('A username is required for password credentials.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const entry = await adapter.importGit(trimmed, {
        label: saveAs.trim() || undefined,
        ref: ref.trim() || undefined,
        credential: credOpen ? { kind: credKind, username: username.trim() || undefined, secret } : undefined,
        saveCredential: credOpen && saveCred,
      });
      onImported(entry);
    } catch (e) {
      setError(e instanceof LibraryError ? e.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  if (busy) {
    return (
      <div className="upload-progress-card">
        <div className="upload-progress-head">
          <GitBranch size={16} />
          <span className="upload-file-name">Cloning {lookup?.repo_name || url}…</span>
        </div>
        <div className="upload-bar">
          <div className="upload-bar-fill indeterminate" />
        </div>
      </div>
    );
  }

  return (
    <div className="import-form">
      <label className="upload-field">
        <span>Repository URL</span>
        <input
          type="text"
          className="text-input"
          placeholder={mode === 'ssh' ? 'git@github.com:owner/repo.git' : 'https://github.com/owner/repo'}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoFocus
          spellCheck={false}
        />
      </label>

      <div className={`import-status${lookupError ? ' bad' : ''}`}>
        {checking && <SpinnerGap size={13} className="spin" />}
        {lookupError && <WarningCircle size={13} />}
        {lookup && !lookupError && (lookup.credential ? <CheckCircle size={13} /> : <GitBranch size={13} />)}
        <span>
          {lookupError ||
            (checking
              ? 'Checking credentials…'
              : lookup
                ? lookup.credential
                  ? `Using stored ${describeCredential(lookup.credential)} for ${lookup.host}`
                  : `No stored credentials for ${lookup.host} — public repositories import without any`
                : mode === 'ssh'
                  ? 'Paste the repository URL — SSH keys saved for the host are used automatically'
                  : 'Paste the repository URL — credentials saved for the host are used automatically')}
        </span>
        {lookup && (
          <button type="button" className="btn btn-sm" onClick={() => { setCredOpen(!credOpen); setTestResult(null); }}>
            {credOpen ? 'Hide' : lookup.credential ? 'Use different' : 'Add credentials'}
          </button>
        )}
      </div>

      {credOpen && (
        <div className="import-cred">
          {mode === 'https' && (
            <div className="import-cred-kind">
              {(['pat', 'password'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`import-kind-btn${credKind === k ? ' active' : ''}`}
                  onClick={() => { setCredKind(k); setTestResult(null); }}
                >
                  {k === 'pat' ? 'Access token (PAT)' : 'Username + password'}
                </button>
              ))}
            </div>
          )}
          {credKind === 'ssh_key' ? (
            <label className="upload-field">
              <span>SSH private key (passphrase-less)</span>
              <textarea
                className="import-key-input"
                placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----'}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                rows={5}
                spellCheck={false}
              />
            </label>
          ) : (
            <>
              <label className="upload-field">
                <span>Username{credKind === 'pat' ? ' (optional)' : ''}</span>
                <input
                  type="text"
                  className="text-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
              <label className="upload-field">
                <span>{credKind === 'pat' ? 'Access token' : 'Password'}</span>
                <input
                  type="password"
                  className="text-input"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoComplete="off"
                />
              </label>
            </>
          )}
          {host && canSave && (
            <label className="import-check">
              <input
                type="checkbox"
                checked={saveCred}
                onChange={(e) => setSaveCred(e.target.checked)}
              />
              Save for {host} (encrypted, reused for every repo on this host)
            </label>
          )}
          <div className="import-cred-actions">
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleTest}
              disabled={!url.trim() || !credComplete || testing}
            >
              <TestTube size={13} /> {testing ? 'Testing…' : 'Test access'}
            </button>
            {testResult && (
              <span className={`import-test-result ${testResult.ok ? 'ok' : 'bad'}`}>
                {testResult.ok ? 'Access verified' : testResult.detail || 'Access failed'}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="import-fields-row">
        <label className="upload-field">
          <span>Save as</span>
          <input
            type="text"
            className="text-input"
            value={saveAs}
            placeholder={lookup?.repo_name || 'Repository name'}
            onChange={(e) => {
              saveAsDirty.current = true;
              setSaveAs(e.target.value);
            }}
          />
        </label>
        <label className="upload-field">
          <span>Branch or tag (optional)</span>
          <input
            type="text"
            className="text-input"
            value={ref}
            placeholder="default branch"
            onChange={(e) => setRef(e.target.value)}
            spellCheck={false}
          />
        </label>
      </div>

      {error && (
        <div className="upload-error">
          <strong>Couldn't import:</strong> {error}
        </div>
      )}

      <div className="upload-actions">
        <button
          type="button"
          className="btn btn-start"
          onClick={handleImport}
          disabled={!url.trim() || !credComplete}
        >
          <GitBranch size={14} /> Import &amp; open
        </button>
      </div>
    </div>
  );
}

// ─── Forgejo flow (vski.sh) ──────────────────────────────────────────────────
// The Forgejo API archive download: a one-shot zip snapshot (no git history,
// no git binary needed on the api). Public repos import anonymously; a PAT
// unlocks private ones — inline or stored for the instance's host.
function ForgejoImportForm({
  adapter,
  onImported,
}: {
  adapter: GitImportAdapter;
  onImported: (entry: LibraryEntry) => void;
}) {
  const [url, setUrl] = useState('');
  const [lookup, setLookup] = useState<GitLookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [saveToken, setSaveToken] = useState(true);
  const [canSave, setCanSave] = useState(false);
  const [saveAs, setSaveAs] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lookupSeq = useRef(0);
  const saveAsDirty = useRef(false);

  useEffect(() => {
    adapter
      .listGitCredentials()
      .then((s) => setCanSave(s.enabled))
      .catch(() => setCanSave(false));
  }, [adapter]);

  const debouncedUrl = useDebounced(url, 350);
  useEffect(() => {
    const seq = ++lookupSeq.current;
    const trimmed = debouncedUrl.trim();
    if (!trimmed) {
      setLookup(null);
      setLookupError(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    adapter
      .gitLookup(trimmed)
      .then((lk) => {
        if (seq !== lookupSeq.current) return;
        setLookup(lk);
        setLookupError(null);
        if (!saveAsDirty.current) setSaveAs(lk.repo_name);
      })
      .catch((e) => {
        if (seq !== lookupSeq.current) return;
        setLookup(null);
        setLookupError(e instanceof LibraryError ? e.message : 'Could not parse that URL.');
      })
      .finally(() => {
        if (seq === lookupSeq.current) setChecking(false);
      });
  }, [debouncedUrl, adapter]);

  const host = lookup?.host ?? null;
  const storedPat = lookup?.credential?.kind === 'pat' ? lookup.credential : null;

  const handleImport = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const entry = await adapter.importForgejo(trimmed, {
        label: saveAs.trim() || undefined,
        ref: ref.trim() || undefined,
        token: tokenOpen ? token.trim() || undefined : undefined,
        saveToken: tokenOpen && saveToken,
        username: tokenOpen ? username.trim() || undefined : undefined,
      });
      onImported(entry);
    } catch (e) {
      setError(e instanceof LibraryError ? e.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  if (busy) {
    return (
      <div className="upload-progress-card">
        <div className="upload-progress-head">
          <FileArchive size={16} />
          <span className="upload-file-name">Downloading {lookup?.repo_name || url} archive…</span>
        </div>
        <div className="upload-bar">
          <div className="upload-bar-fill indeterminate" />
        </div>
      </div>
    );
  }

  return (
    <div className="import-form">
      <label className="upload-field">
        <span>Repository URL</span>
        <input
          type="text"
          className="text-input"
          placeholder="https://vski.sh/x/agent"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoFocus
          spellCheck={false}
        />
      </label>

      <div className={`import-status${lookupError ? ' bad' : ''}`}>
        {checking && <SpinnerGap size={13} className="spin" />}
        {lookupError && <WarningCircle size={13} />}
        {lookup && !lookupError && (storedPat ? <CheckCircle size={13} /> : <FileArchive size={13} />)}
        <span>
          {lookupError ||
            (checking
              ? 'Checking credentials…'
              : lookup
                ? storedPat
                  ? `Using stored ${describeCredential(storedPat)} for ${lookup.host}`
                  : `Public repositories on ${lookup.host} import without a token — a personal access token unlocks private ones`
                : 'Paste the repository URL (an archive snapshot is downloaded — no git history)')}
        </span>
        {lookup && (
          <button type="button" className="btn btn-sm" onClick={() => setTokenOpen(!tokenOpen)}>
            {tokenOpen ? 'Hide' : storedPat ? 'Use a different token' : 'Add token'}
          </button>
        )}
      </div>

      {tokenOpen && (
        <div className="import-cred">
          <label className="upload-field">
            <span>Personal access token</span>
            <input
              type="password"
              className="text-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
              placeholder="Forgejo PAT with read:repository scope"
            />
          </label>
          <label className="upload-field">
            <span>Username (optional — for git clones with this token)</span>
            <input
              type="text"
              className="text-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          {host && canSave && (
            <label className="import-check">
              <input
                type="checkbox"
                checked={saveToken}
                onChange={(e) => setSaveToken(e.target.checked)}
              />
              Save for {host} (encrypted, reused for every repo on this host)
            </label>
          )}
        </div>
      )}

      <div className="import-fields-row">
        <label className="upload-field">
          <span>Save as</span>
          <input
            type="text"
            className="text-input"
            value={saveAs}
            placeholder={lookup?.repo_name || 'Repository name'}
            onChange={(e) => {
              saveAsDirty.current = true;
              setSaveAs(e.target.value);
            }}
          />
        </label>
        <label className="upload-field">
          <span>Branch or tag (optional)</span>
          <input
            type="text"
            className="text-input"
            value={ref}
            placeholder="default branch"
            onChange={(e) => setRef(e.target.value)}
            spellCheck={false}
          />
        </label>
      </div>

      {error && (
        <div className="upload-error">
          <strong>Couldn't import:</strong> {error}
        </div>
      )}

      <div className="upload-actions">
        <button
          type="button"
          className="btn btn-start"
          onClick={handleImport}
          disabled={!url.trim()}
        >
          <FileArchive size={14} /> Import &amp; open
        </button>
      </div>
    </div>
  );
}

// ─── Shared presentational pieces ───────────────────────────────────────────
function LibraryRow({
  entry,
  children,
  tagging,
}: {
  entry: LibraryEntry;
  children: React.ReactNode;
  tagging?: boolean;
}) {
  return (
    <div className={`lib-row${tagging ? ' tagging' : ''}`}>
      <span className="lib-row-icon">
        <span className="lib-row-glyph">▤</span>
      </span>
      <span className="lib-row-main">
        <span className="lib-row-title">{entry.label}</span>
        <span className="lib-row-desc">{entry.description || 'No description'}</span>
        {entry.tags.length > 0 && (
          <span className="lib-row-tags">
            {entry.tags.map((t) => (
              <span key={t} className="tag-chip static"><Tag size={10} /> {t}</span>
            ))}
          </span>
        )}
      </span>
      <span className="lib-row-meta">
        {entry.nodeCount > 0 && (
          <span className="lib-row-meta-pill">{entry.nodeCount} actor{entry.nodeCount === 1 ? '' : 's'}</span>
        )}
      </span>
      <span className="lib-row-actions">{children}</span>
    </div>
  );
}

/** Inline tag editor: add tags with Enter, remove with a click, save/cancel. */
function TagEditor({
  tags,
  onSave,
  onCancel,
}: {
  tags: string[];
  onSave: (tags: string[]) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [current, setCurrent] = useState<string[]>(tags);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(t);
  }, []);

  const add = () => {
    const norm = draft.trim().toLowerCase().replace(/\s+/g, '-');
    if (!norm || current.includes(norm)) {
      setDraft('');
      return;
    }
    setCurrent((c) => [...c, norm]);
    setDraft('');
  };

  return (
    <div className="tag-editor" onClick={(e) => e.stopPropagation()}>
      <div className="tag-editor-chips">
        {current.map((t) => (
          <span key={t} className="tag-chip removable">
            <Tag size={10} /> {t}
            <button type="button" aria-label={`Remove ${t}`} onClick={() => setCurrent((c) => c.filter((x) => x !== t))}>
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="tag-editor-input"
          value={draft}
          placeholder="Add tag…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            } else if (e.key === 'Escape') {
              onCancel();
            }
          }}
        />
      </div>
      <div className="tag-editor-actions">
        <button type="button" className="btn btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-sm btn-start" onClick={() => onSave(current)}>
          <FloppyDisk size={12} /> Save tags
        </button>
      </div>
    </div>
  );
}

/** An empty ScenarioBundle for the "build from scratch" entry. Has no nodes,
 *  no edges, no metrics — the user adds them via the graph editor. The id is a
 *  fresh uuid each call so two "New" actions never collide. */
export function emptyBundle(): ScenarioBundle {
  return {
    id: uuid(),
    label: 'Untitled project',
    graph: { nodes: [], edges: [] },
    metrics: [],
    initialParams: {},
    initialStocks: {},
  };
}
